import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  addDoc
} from 'firebase/firestore';
import { db, handleFirebaseError } from '../firebase.config';

// 🔄 User Migration Utilities for ACCTOO
export class UserMigrationUtils {
  
  // Migrate existing user to new system
  static async migrateExistingUser(userId, userData, companyId = null) {
    try {
      console.log('🔄 Migrating existing user:', userId);
      
      // 1. Create/Update user profile
      const userProfile = await this.createOrUpdateUserProfile(userId, userData);
      
      // 2. If companyId provided, add user to company
      if (companyId) {
        await this.addUserToCompany(userId, companyId, userData.role || 'employee');
      }
      
      // 3. Create MPIN settings if not exists
      await this.initializeMPINSettings(userId);
      
      // 4. Create user companies mapping
      await this.createUserCompaniesMapping(userId, companyId);
      
      console.log('✅ User migration completed:', userId);
      return { success: true, profile: userProfile };
      
    } catch (error) {
      console.error('❌ User migration failed:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Create or update user profile
  static async createOrUpdateUserProfile(userId, userData) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      const profileData = {
        uid: userId,
        email: userData.email || '',
        displayName: userData.displayName || userData.name || '',
        phoneNumber: userData.phoneNumber || '',
        role: userData.role || 'user',
        status: 'active',
        emailVerified: userData.emailVerified || false,
        updatedAt: serverTimestamp(),
        migratedAt: serverTimestamp(),
        ...userData
      };
      
      if (userSnap.exists()) {
        // Update existing profile
        await updateDoc(userRef, profileData);
        console.log('🔄 Updated existing user profile:', userId);
      } else {
        // Create new profile
        profileData.createdAt = serverTimestamp();
        profileData.lastLoginAt = serverTimestamp();
        await setDoc(userRef, profileData);
        console.log('🆕 Created new user profile:', userId);
      }
      
      return profileData;
      
    } catch (error) {
      handleFirebaseError(error, 'User profile creation/update');
      throw error;
    }
  }
  
  // Add user to company with role
  static async addUserToCompany(userId, companyId, role = 'employee') {
    try {
      const companyUserRef = doc(db, 'companies', companyId, 'employees', userId);
      const companyUserSnap = await getDoc(companyUserRef);
      
      const employeeData = {
        uid: userId,
        role: role,
        status: 'active',
        addedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        permissions: this.getDefaultPermissions(role)
      };
      
      if (companyUserSnap.exists()) {
        // Update existing employee record
        await updateDoc(companyUserRef, {
          ...employeeData,
          updatedAt: serverTimestamp()
        });
        console.log('🔄 Updated existing employee record:', userId, companyId);
      } else {
        // Create new employee record
        await setDoc(companyUserRef, employeeData);
        console.log('🆕 Created new employee record:', userId, companyId);
      }
      
      return { success: true };
      
    } catch (error) {
      handleFirebaseError(error, 'Add user to company');
      throw error;
    }
  }
  
  // Initialize MPIN settings for user
  static async initializeMPINSettings(userId) {
    try {
      const settingsRef = doc(db, 'artifacts', 'acc-app-e5316', 'users', userId, 'Settings', 'default');
      const settingsSnap = await getDoc(settingsRef);
      
      if (!settingsSnap.exists()) {
        // Create default MPIN settings
        const defaultSettings = {
          mpin: null, // No MPIN set initially
          mpinAccessControl: {
            // Default access control - all pages require MPIN initially
            'Dashboard': {
              '_directAccess': true
            },
            'Sales': {
              'Bills': true,
              'Parties': true,
              'Reports': true
            },
            'Purchases': {
              'Bills': true,
              'Parties': true,
              'Reports': true
            },
            'Expenses': {
              '_directAccess': true
            },
            'Payments': {
              '_directAccess': true
            },
            'Items': {
              '_directAccess': true
            },
            'Parties': {
              '_directAccess': true
            },
            'Reports': {
              '_directAccess': true
            },
            'Settings': {
              '_directAccess': true
            }
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await setDoc(settingsRef, defaultSettings);
        console.log('🆕 Created default MPIN settings for user:', userId);
      }
      
      return { success: true };
      
    } catch (error) {
      handleFirebaseError(error, 'MPIN settings initialization');
      throw error;
    }
  }
  
  // Create user companies mapping
  static async createUserCompaniesMapping(userId, companyId) {
    try {
      const userCompaniesRef = doc(db, 'user_companies', userId);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      
      // Use legacy structure for owner-employee relationship importance
      // Use manual ISO timestamp to avoid Firebase conflicts
      const now = new Date().toISOString();
      const userCompaniesData = {
        owned_companies: companyId ? [{ 
          companyId, 
          role: 'owner', 
          created_at: now,
          status: 'active'
        }] : [],
        employed_companies: [],
        activeCompany: companyId || null,
        updatedAt: now
      };
      
      if (userCompaniesSnap.exists()) {
        // Update existing mapping
        const existingData = userCompaniesSnap.data();
        const ownedCompanies = existingData.owned_companies || [];
        
        // Check if company already exists in owned_companies
        const companyExists = ownedCompanies.some(c => c.companyId === companyId);
        if (companyId && !companyExists) {
          ownedCompanies.push({ 
            companyId, 
            role: 'owner', 
            created_at: now,
            status: 'active'
          });
        }
        
        await updateDoc(userCompaniesRef, {
          ...existingData,
          owned_companies: ownedCompanies,
          updatedAt: now
        });
        console.log('🔄 Updated user companies mapping (legacy):', userId);
      } else {
        // Create new mapping
        userCompaniesData.createdAt = now;
        await setDoc(userCompaniesRef, userCompaniesData);
        console.log('🆕 Created user companies mapping (legacy):', userId);
      }
      
      return { success: true };
      
    } catch (error) {
      handleFirebaseError(error, 'User companies mapping creation');
      throw error;
    }
  }
  
  // Get user's companies
  static async getUserCompanies(userId) {
    try {
      const userCompaniesRef = doc(db, 'user_companies', userId);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      
      if (userCompaniesSnap.exists()) {
        const data = userCompaniesSnap.data();
        return { success: true, data };
      }
      
      return { success: false, error: 'User companies not found' };
      
    } catch (error) {
      handleFirebaseError(error, 'Get user companies');
      return { success: false, error: error.message };
    }
  }
  
  // Switch user's active company
  static async switchActiveCompany(userId, companyId) {
    try {
      const userCompaniesRef = doc(db, 'user_companies', userId);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      
      if (userCompaniesSnap.exists()) {
        const data = userCompaniesSnap.data();
        if (data.companies.includes(companyId)) {
          await updateDoc(userCompaniesRef, {
            activeCompany: companyId,
            updatedAt: serverTimestamp()
          });
          console.log('🔄 Switched active company for user:', userId, companyId);
          return { success: true };
        } else {
          return { success: false, error: 'User does not have access to this company' };
        }
      }
      
      return { success: false, error: 'User companies mapping not found' };
      
    } catch (error) {
      handleFirebaseError(error, 'Switch active company');
      return { success: false, error: error.message };
    }
  }
  
  // Get company employees
  static async getCompanyEmployees(companyId) {
    try {
      const employeesQuery = query(
        collection(db, 'companies', companyId, 'employees'),
        where('status', '==', 'active')
      );
      
      const employeesSnap = await getDocs(employeesQuery);
      const employees = [];
      
      employeesSnap.forEach(doc => {
        employees.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, data: employees };
      
    } catch (error) {
      handleFirebaseError(error, 'Get company employees');
      return { success: false, error: error.message };
    }
  }
  
  // Get company info
  static async getCompanyInfo(companyId) {
    try {
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyRef);
      
      if (companySnap.exists()) {
        return { success: true, data: companySnap.data() };
      }
      
      return { success: false, error: 'Company not found' };
      
    } catch (error) {
      handleFirebaseError(error, 'Get company info');
      return { success: false, error: error.message };
    }
  }
  
  // Create company info
  static async createCompanyInfo(companyId, companyData) {
    try {
      const companyRef = doc(db, 'companies', companyId);
      await setDoc(companyRef, {
        ...companyData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log('🆕 Created company info:', companyId);
      return { success: true };
      
    } catch (error) {
      handleFirebaseError(error, 'Create company info');
      return { success: false, error: error.message };
    }
  }
  
  // Get default permissions based on role
  static getDefaultPermissions(role) {
    const permissions = {
      owner: ['all'],
      manager: ['read', 'write', 'delete', 'manage_employees'],
      employee: ['read', 'write'],
      viewer: ['read']
    };
    
    return permissions[role] || ['read'];
  }
  
  // Bulk migrate users from existing system
  static async bulkMigrateUsers(usersData) {
    try {
      console.log('🔄 Starting bulk user migration for', usersData.length, 'users');
      
      const results = [];
      for (const userData of usersData) {
        try {
          const result = await this.migrateExistingUser(
            userData.userId, 
            userData, 
            userData.companyId
          );
          results.push({ userId: userData.userId, ...result });
        } catch (error) {
          results.push({ 
            userId: userData.userId, 
            success: false, 
            error: error.message 
          });
        }
      }
      
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      console.log(`✅ Bulk migration completed: ${successful} successful, ${failed} failed`);
      
      return { success: true, results, summary: { successful, failed } };
      
    } catch (error) {
      console.error('❌ Bulk migration failed:', error);
      return { success: false, error: error.message };
    }
  }
}

export default UserMigrationUtils;
